import { LegalShell, type LegalLang } from './legalShared'

const APP = 'Provisyer'

// "Teslimat - İade" — how a digital service is delivered, and under what terms
// it can be cancelled or refunded. Required by acquiring banks and by consumer
// law. Everything promised here must be backed by an actual implementation:
// the one-click cancel exists; refunds are processed manually by an admin.
export default function DeliveryReturnPage({ lang = 'tr' }: { lang?: LegalLang }) {
  return lang === 'en' ? <DeliveryEN /> : <DeliveryTR />
}

function DeliveryTR() {
  return (
    <LegalShell
      lang="tr"
      page="delivery"
      title="Teslimat ve İade Koşulları"
      updated="Son güncelleme: 13 Ağustos 2026"
    >
      <p>
        {APP} üzerinden satılan tüm ürünler dijital abonelik hizmetleridir. Fiziksel ürün satışı ve kargo gönderimi
        yapılmaz. Bu sayfa, hizmetin nasıl sunulduğunu ve iptal/iade koşullarını açıklar.
      </p>

      <h2>1. Teslimat</h2>
      <h3>1.1. Teslimat şekli</h3>
      <p>
        Satın aldığınız hizmet, {APP} hesabınıza tanımlanarak sunulur. Bir kargo veya fiziksel teslimat süreci yoktur.
        Ödemeniz onaylandığı anda ilgili hizmet otomatik olarak kurulmaya başlar ve panelinizde görünür.
      </p>
      <h3>1.2. Teslimat süresi</h3>
      <ul>
        <li>
          <strong>Yönetilen Odoo kurulumu:</strong> ödeme onayının ardından kurulum otomatik başlar ve tipik olarak
          birkaç dakika içinde tamamlanır. Kurulum adımlarını panelde canlı olarak izleyebilirsiniz.
        </li>
        <li>
          <strong>Mail sunucu eklentisi:</strong> ödeme onayının ardından hesabınızda anında etkinleşir. Alan adı
          doğrulaması (MX, SPF, DKIM, DMARC kayıtları) sizin DNS ayarlarınıza bağlı olduğundan, tam olarak
          çalışır hâle gelmesi DNS yayılım süresine göre değişebilir.
        </li>
      </ul>
      <p>
        Teknik bir aksaklık nedeniyle hizmet açılamazsa en geç <strong>24 saat</strong> içinde durum size bildirilir ve
        sorun giderilir. Giderilemezse ödeme tam olarak iade edilir.
      </p>
      <h3>1.3. Teslimat ücreti</h3>
      <p>Dijital hizmet olduğu için kargo veya teslimat ücreti alınmaz.</p>

      <h2>2. İptal</h2>
      <p>
        Aboneliğinizi istediğiniz zaman, herhangi bir gerekçe göstermeden, panelinizdeki{' '}
        <strong>Faturalandırma</strong> ekranından tek tıkla iptal edebilirsiniz. İptal için bizimle iletişime
        geçmenize gerek yoktur.
      </p>
      <ul>
        <li>İptal, içinde bulunduğunuz ödeme döneminin sonunda yürürlüğe girer.</li>
        <li>Dönem sonuna kadar hizmeti kullanmaya devam edersiniz.</li>
        <li>Dönem sonunda yenileme tahsilatı yapılmaz ve hizmet durdurulur.</li>
        <li>
          Verilerinizi hizmet durdurulmadan önce yedek alma özelliğiyle indirebilirsiniz. Durdurmadan sonraki veri
          saklama süresi <a href="/hizmet-sartlari">Hizmet Şartları</a> sayfasında belirtilmiştir.
        </li>
      </ul>

      <h2>3. İade</h2>
      <h3>3.1. Cayma hakkı ve istisnası</h3>
      <p>
        Dijital hizmetler, ödeme onayının hemen ardından ifa edildiğinden, Mesafeli Sözleşmeler Yönetmeliği&apos;nin
        15/1-(ğ) maddesi uyarınca cayma hakkı kapsamı dışındadır. Bu istisnayı sipariş sırasında Mesafeli Satış
        Sözleşmesi ile birlikte onaylarsınız.
      </p>
      <h3>3.2. Uyguladığımız iade koşulları</h3>
      <p>
        Yasal istisnaya rağmen, aşağıdaki durumlarda iyi niyet gereği iade yapıyoruz:
      </p>
      <ul>
        <li>
          <strong>Hizmet hiç kullanılmadıysa:</strong> satın alma tarihinden itibaren 14 gün içinde talep etmeniz ve
          kurulumu hiç kullanmamış olmanız hâlinde ödemenin tamamı iade edilir.
        </li>
        <li>
          <strong>Hizmet sunulamadıysa:</strong> teknik nedenlerle hizmeti sağlayamadığımız veya ilan ettiğimiz
          niteliklere uygun sunamadığımız durumlarda, kullanılmayan döneme ait tutar iade edilir.
        </li>
        <li>
          <strong>Mükerrer tahsilat:</strong> aynı hizmet için sehven birden fazla tahsilat yapılmışsa fazla tutar
          tamamen iade edilir.
        </li>
      </ul>
      <p>
        Kullanılmış bir abonelik döneminin kısmi iadesi yapılmaz; iptal ettiğinizde dönem sonuna kadar hizmeti
        kullanmaya devam edersiniz.
      </p>
      <h3>3.3. İade süreci</h3>
      <ul>
        <li>İade talebinizi <a href="/hakkimizda#iletisim">iletişim</a> sayfamızdaki e-posta adresine iletin.</li>
        <li>Talebinizi en geç 3 iş günü içinde değerlendirip sonucu bildiririz.</li>
        <li>
          Onaylanan iadeler, ödemenin yapıldığı karta, onay tarihinden itibaren <strong>14 gün</strong> içinde
          yapılır. Tutarın kart ekstrenize yansıması bankanızın işlem süresine bağlıdır.
        </li>
        <li>İade, ödemenin yapıldığı yöntemden başka bir kanala (havale, farklı kart) yapılamaz.</li>
      </ul>

      <h2>4. İletişim</h2>
      <p>
        İptal, iade ve tüm diğer talepleriniz için <a href="/hakkimizda#iletisim">Hakkımızda</a> sayfasındaki
        iletişim bilgilerinden bize ulaşabilirsiniz.
      </p>
    </LegalShell>
  )
}

function DeliveryEN() {
  return (
    <LegalShell
      lang="en"
      page="delivery"
      title="Delivery and Returns"
      updated="Last updated: 13 August 2026"
    >
      <p>
        Everything sold on {APP} is a digital subscription service. There are no physical goods and no shipping. This
        page explains how the service is delivered and under what terms it can be cancelled or refunded.
      </p>

      <h2>1. Delivery</h2>
      <p>
        The service is delivered by activating it on your {APP} account. Provisioning starts automatically the moment
        payment is confirmed and typically completes within a few minutes; you can watch each step live in your
        dashboard. The mail add-on activates instantly, though full operation depends on your own DNS records
        propagating.
      </p>
      <p>
        If a technical fault prevents activation, we will notify you within <strong>24 hours</strong> and resolve it.
        If we cannot, the payment is refunded in full. No delivery or shipping fee is charged.
      </p>

      <h2>2. Cancellation</h2>
      <p>
        You can cancel at any time, without giving a reason, with one click from the <strong>Billing</strong> screen.
        Cancellation takes effect at the end of the current billing period: you keep using the service until then, no
        renewal is charged, and the service stops afterwards. Export your data with the backup feature before the
        period ends.
      </p>

      <h2>3. Refunds</h2>
      <p>
        Digital services performed immediately are excluded from the statutory right of withdrawal under Article
        15/1(ğ) of the Turkish Distance Contracts Regulation, which you approve at checkout. Regardless, we refund in
        the following cases:
      </p>
      <ul>
        <li><strong>Service never used:</strong> full refund if requested within 14 days of purchase and the deployment was never used.</li>
        <li><strong>Service not delivered:</strong> the unused portion is refunded if we could not provide the service as described.</li>
        <li><strong>Duplicate charge:</strong> any amount charged more than once for the same service is refunded in full.</li>
      </ul>
      <p>Partial refunds are not issued for a billing period that has been used.</p>
      <p>
        Send refund requests to the email address on our <a href="/about#contact">About</a> page. We respond within 3
        business days and, once approved, return the amount to the original card within <strong>14 days</strong>.
        Refunds cannot be paid to a different card or by bank transfer.
      </p>
    </LegalShell>
  )
}
